import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { APPLY_BUTTON_STYLE } from "./reportCommon";
import { ClearVehicleHaltPoint } from "./ReportApi";

const NAVY = "#003366";

const navyHeaderStyle = {
  backgroundColor: NAVY,
  color: "#FFFFFF",
  borderBottom: "none",
  padding: "14px 20px",
};

const ModalCloseIcon = ({ onClick, light }) => (
  <i
    className="fa-solid fa-xmark close-container"
    onClick={onClick}
    style={light ? { color: "#FFFFFF", cursor: "pointer" } : undefined}
  />
);

export const BalancePointActionModal = ({ show, point, onClose, onSuccess }) => {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [remarks, setRemarks] = useState("");
  const [imageError, setImageError] = useState("");
  const [remarksError, setRemarksError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    setImageFile(null);
    setImagePreview("");
    setRemarks("");
    setImageError("");
    setRemarksError("");
    setSubmitting(false);
  }, [show, point?.id, point?.stoppageId]);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImageFile(null);
      setImagePreview("");
      return;
    }

    if (!file.type.startsWith("image/")) {
      setImageError("Please upload a valid image file.");
      setImageFile(null);
      setImagePreview("");
      return;
    }

    setImageError("");
    setImageFile(file);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextImageError = imageFile ? "" : "Please upload an image.";
    const nextRemarksError = remarks.trim() ? "" : "Please enter remarks.";

    setImageError(nextImageError);
    setRemarksError(nextRemarksError);

    if (nextImageError || nextRemarksError) return;

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("stoppageId", point?.stoppageId || point?.id || "");
      formData.append("actualLatitude", point?.latitude || point?.actualLatitude || "");
      formData.append("actualLongitude", point?.longitude || point?.actualLongitude || "");
      formData.append("remark", remarks.trim());
      formData.append("image", imageFile);

      const res = await ClearVehicleHaltPoint(formData);
      if (res?.status === 200 || res?.data?.success || res?.data?.status) {
        toast.success(res?.data?.message || "Balance point action cleared successfully.");
      } else {
        toast.success("Balance point action submitted successfully.");
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error clearing vehicle halt point:", error);
      toast.error(
        error?.response?.data?.message || "Failed to submit balance point action."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header style={navyHeaderStyle}>
        <Modal.Title className="fw-bold text-white fs-5 mb-0">
          Balance Point Action
        </Modal.Title>
        <ModalCloseIcon onClick={onClose} light />
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body className="px-4 py-4">
          <div className="mb-3">
            <span className="fw-semibold">Halt Point</span>
            <span className="mx-2">:</span>
            <span>{point?.haltPoint || "—"}</span>
          </div>
          <div className="mb-3">
            <span className="fw-semibold">Latitude</span>
            <span className="mx-2">:</span>
            <span>{point?.latitude || "—"}</span>
          </div>
          <div className="mb-3">
            <span className="fw-semibold">Longitude</span>
            <span className="mx-2">:</span>
            <span>{point?.longitude || "—"}</span>
          </div>
          <div className="mb-3">
            <span className="fw-semibold">Status</span>
            <span className="mx-2">:</span>
            <span>{point?.status || "—"}</span>
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">
              Image <span className="text-danger">*</span>
            </label>
            <input
              type="file"
              className="form-control"
              accept="image/png, image/jpeg, image/jpg"
              onChange={handleImageChange}
            />
            {imagePreview ? (
              <div
                className="border rounded d-flex align-items-center justify-content-center bg-light overflow-hidden mt-2"
                style={{ minHeight: 160 }}
              >
                <img
                  src={imagePreview}
                  alt="Balance point preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 220,
                    objectFit: "cover",
                  }}
                />
              </div>
            ) : null}
            {imageError ? (
              <div className="text-danger small mt-1">{imageError}</div>
            ) : null}
          </div>

          <div className="mb-1">
            <label className="form-label fw-semibold">
              Remarks <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Enter remarks"
              value={remarks}
              onChange={(event) => {
                setRemarks(event.target.value);
                if (event.target.value.trim()) setRemarksError("");
              }}
            />
            {remarksError ? (
              <div className="text-danger small mt-1">{remarksError}</div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 px-4 pb-4 d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary px-4"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={{ ...APPLY_BUTTON_STYLE, opacity: submitting ? 0.7 : 1 }}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
